import { BASE_URL } from "../Constants";
import { handleLogout } from "../components/auth/authHelpers";
import api from "../components/login/interceptors";
import { panelistDisplayName } from "./panelists";

/**
 * SiftHire scheduling API (resume-parser). Only the fields the frontend reads are listed.
 *
 * @typedef {"ai_interviewer"|"panelist_with_ai"|null} InterviewAIMode null: panelist only
 *
 * @typedef {{ candidate_id: string, date: string, start_time: string, end_time: string, end_date?: string }} InterviewSlot
 *   end_date (defaults to date) for an interview that runs past midnight
 *
 * @typedef {{
 *   interview_id: string, status: string, jd_id: string, jd_title?: string, round_id: string, round_name?: string,
 *   candidate: { candidate_id: string, name?: string, email?: string },
 *   panelists: { panelist_id: string, name?: string, last_name?: string, email?: string }[],
 *   ai_mode: InterviewAIMode, timezone: string, start_utc?: string, end_utc?: string, duration_minutes?: number,
 *   calendar_provider?: "google"|"microsoft"|null, meeting_link?: string
 * }} Interview list and detail payloads
 *
 * `panelist_ids` may be [] only when `ai_mode` is "ai_interviewer"; otherwise 422 VALIDATION_ERROR
 * with details.field = "panelist_ids".
 *
 * @typedef {{ jd_id: string, round_id: string, candidate_ids: string[], panelist_ids: string[],
 *   ai_mode?: InterviewAIMode, timezone: string, calendar_provider?: "google"|"microsoft",
 *   duration_minutes?: number, slots: InterviewSlot[] }} CreateInterviewsRequest POST /interviews; ai_mode applies to every candidate
 *
 * @typedef {{ date: string, start_time: string, end_time: string, end_date?: string, panelist_ids: string[], timezone: string,
 *   ai_mode?: InterviewAIMode, calendar_provider?: "google"|"microsoft", duration_minutes?: number }} ScheduleInterviewRequest
 *   POST /interviews/{id}/schedule; omitting ai_mode keeps the current mode
 *
 * @typedef {{ date: string, start_time: string, end_time: string, end_date?: string, panelist_ids: string[], timezone: string,
 *   ai_mode?: InterviewAIMode, reason?: string }} RescheduleInterviewRequest
 *   POST /interviews/{id}/reschedule; omitting ai_mode keeps the current mode
 *
 * @typedef {{ ai_mode: InterviewAIMode }} SetInterviewAIModeRequest POST /interviews/{id}/ai-mode
 * @typedef {{ interview: Interview }} SetInterviewAIModeResponse
 */

export const INTERVIEW_STATUS = {
  SCHEDULED: "scheduled",
  NOT_SCHEDULED: "not_scheduled",
};

// Backend statuses that count as "Scheduled" in the UI
const ACTIVE_INTERVIEW_STATUSES = ["scheduled", "rescheduled"];
// Created without a time slot; completed via POST /interviews/{id}/schedule
const PENDING_INTERVIEW_STATUS = "pending_candidate_selection";

export const INTEGRATION_STATUS = {
  CONNECTED: "connected",
  REAUTH_REQUIRED: "reauth_required",
  NOT_CONNECTED: "not_connected",
};

// Path segment under /api/{provider}/... for each meeting integration
export const INTEGRATION_PROVIDER = {
  GOOGLE: "google",
  MICROSOFT: "microsoft",
};

// Kept so existing Google-only screens can keep calling the same helpers
export const GOOGLE_STATUS = INTEGRATION_STATUS;

export const CALENDAR_PROVIDERS = [
  {
    id: INTEGRATION_PROVIDER.GOOGLE,
    name: "Google Calendar",
    connectLabel: "Connect Google Calendar",
    reconnectLabel: "Reconnect Google Calendar",
    badge: "Meet",
  },
  {
    id: INTEGRATION_PROVIDER.MICROSOFT,
    name: "Microsoft 365",
    connectLabel: "Connect Microsoft 365",
    reconnectLabel: "Reconnect Microsoft 365",
    badge: "Teams",
  },
];

const PAGE_LIMIT = 100;
const MAX_PAGES = 20;

const normalizeRound = (round) => ({
  id: round.round_id,
  name: round.name || "",
  isDefault: !!round.is_default,
});

const normalizePanelist = (panelist) => ({
  id: panelist.panelist_id,
  name: panelistDisplayName(panelist) || panelist.email || "",
  email: panelist.email || "",
});

// e.g. "Oct 8, 2026, 10:00 AM – 11:00 AM"
export const formatInterviewSlot = (start, end) => {
  const startDate = new Date(start);
  if (!start || Number.isNaN(startDate.getTime())) return "";
  const timeOptions = { hour: "2-digit", minute: "2-digit" };
  const day = startDate.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  const startLabel = startDate.toLocaleTimeString("en-US", timeOptions);
  const endDate = new Date(end);
  const endLabel = end && !Number.isNaN(endDate.getTime())
    ? ` – ${endDate.toLocaleTimeString("en-US", timeOptions)}`
    : "";
  return `${day}, ${startLabel}${endLabel}`;
};

export const normalizeInterview = (interview) => ({
  id: interview.interview_id || interview.id,
  jdId: interview.jd_id || interview.job_id,
  jdTitle: interview.jd_title || interview.job_title || "",
  candidateId: interview.candidate?.candidate_id || interview.candidate_id,
  roundId: interview.round_id,
  roundName: interview.round_name || interview.round || "",
  interviewerName: (interview.panelists || [])
    .map((panelist) => panelistDisplayName(panelist))
    .filter(Boolean)
    .join(", ") || interview.interviewer_name || "",
  panelistNames: (interview.panelists || []).map((panelist) => panelistDisplayName(panelist)).filter(Boolean),
  timezone: interview.timezone || "",
  startTime: interview.start_utc || interview.start_time,
  endTime: interview.end_utc || interview.end_time,
  startLocal: interview.start_local || "",
  endLocal: interview.end_local || "",
  candidateResponse: interview.candidate_response || "",
  candidateName: interview.candidate?.name || "",
  candidateEmail: interview.candidate?.email || "",
  jdUuid: interview.jd_uuid || "",
  calendarProvider: interview.calendar_provider || null,
  aiMode: interview.ai_mode ?? null,
  meetingLink: interview.meeting_link || "",
  location: typeof interview.location === "string" ? interview.location : "",
  calendarSyncError: interview.calendar_sync_error || "",
  // meeting_link, then location. meet_link is deprecated and no longer read.
  link: interview.meeting_link || (typeof interview.location === "string" ? interview.location : "") || "",
  backendStatus: interview.status,
  isPending: interview.status === PENDING_INTERVIEW_STATUS,
  status: ACTIVE_INTERVIEW_STATUSES.includes(interview.status)
    ? INTERVIEW_STATUS.SCHEDULED
    : INTERVIEW_STATUS.NOT_SCHEDULED,
  // Summary kept up to date by the AI interviewer service (missing when AI was never enabled)
  aiInterview: interview.ai_interview || null,
});

// Error shape: { error, code, details } or { error, errors: [{ error }] }
const nestedErrorMessage = (data) => {
  const items = Array.isArray(data?.errors) ? data.errors : [];
  return items.map((item) => item?.error || item?.message).filter(Boolean).join(" ");
};

export const getErrorCode = (error) => error?.response?.data?.code;

// The request field a 422 VALIDATION_ERROR is about (e.g. "panelist_ids", "ai_mode"), or ""
export const validationErrorField = (error) =>
  getErrorCode(error) === "VALIDATION_ERROR" ? error?.response?.data?.details?.field || "" : "";

export const getErrorMessage = (error, fallback) =>
  nestedErrorMessage(error?.response?.data) ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

const CALENDAR_ERROR_COPY = {
  CALENDAR_PROVIDER_REQUIRED: "Choose a calendar.",
  CALENDAR_PROVIDER_INVALID: "Choose Google Calendar or Microsoft 365.",
  CALENDAR_NOT_CONNECTED: "Connect a calendar before scheduling.",
  CALENDAR_PROVIDER_LOCKED: "Can't switch calendar. Cancel and book a new interview.",
  MICROSOFT_NOT_CONNECTED: "Connect Microsoft 365",
  MICROSOFT_REAUTH_REQUIRED: "Reconnect Microsoft 365",
  MICROSOFT_PERMISSION_DENIED: "Reconnect, or ask your Microsoft 365 admin",
  MICROSOFT_API_ERROR: "Microsoft is having trouble, try again",
  MICROSOFT_NOT_CONFIGURED: "Microsoft 365 isn't available right now.",
  CALENDAR_NOT_CONFIGURED: "Microsoft 365 isn't available right now.",
  GOOGLE_NOT_CONNECTED: "Connect Google Calendar before scheduling.",
  GOOGLE_REAUTH_REQUIRED: "Reconnect Google Calendar before scheduling.",
  GOOGLE_API_ERROR: "Google is having trouble, try again",
  GOOGLE_NOT_CONFIGURED: "Google Calendar isn't available right now.",
};

const hasAppError = (data) =>
  Boolean(
    data &&
    typeof data === "object" &&
    (data.code || data.error || data.message || data.detail || (Array.isArray(data.errors) && data.errors.length))
  );

// Calendar failures use 409, not 401. A 401 is an expired app session:
// INVALID_TOKEN, or a bare 401 whose only text is Axios's status line.
export const isSessionExpiry = (error) => {
  if (error?.response?.status !== 401) return false;
  if (error?.response?.data?.code === "INVALID_TOKEN") return true;
  return !hasAppError(error?.response?.data);
};

export const logoutIfSessionExpired = (error) => {
  if (!isSessionExpiry(error)) return false;
  handleLogout();
  return true;
};

export const describeCalendarError = (error, fallback = "Couldn't complete that calendar action.") => {
  const data = error?.response?.data || {};
  const code = data.code || "";
  const details = data.details || {};
  const connectProviders = code === "CALENDAR_NOT_CONNECTED"
    ? (Array.isArray(details.available) ? details.available : [INTEGRATION_PROVIDER.GOOGLE, INTEGRATION_PROVIDER.MICROSOFT])
    : code === "MICROSOFT_NOT_CONNECTED" || code === "MICROSOFT_REAUTH_REQUIRED"
      ? [INTEGRATION_PROVIDER.MICROSOFT]
      : code === "GOOGLE_NOT_CONNECTED" || code === "GOOGLE_REAUTH_REQUIRED"
        ? [INTEGRATION_PROVIDER.GOOGLE]
        : [];

  return {
    code,
    status: error?.response?.status,
    sessionExpired: isSessionExpiry(error),
    message: CALENDAR_ERROR_COPY[code] || getErrorMessage(error, fallback),
    showPicker: code === "CALENDAR_PROVIDER_REQUIRED",
    pickerOptions: Array.isArray(details.connected) ? details.connected : [],
    connectProviders,
    hideMicrosoft: code === "MICROSOFT_NOT_CONFIGURED" || code === "CALENDAR_NOT_CONFIGURED",
    hideGoogle: code === "GOOGLE_NOT_CONFIGURED",
    offerAdminConsent: code === "MICROSOFT_PERMISSION_DENIED" || code === "consent_required",
  };
};

export const microsoftReturnMessage = (status, reason) => {
  if (status === "connected") return { tone: "success", message: "Microsoft 365 connected." };
  if (status === "admin_consented") return { tone: "success", message: "Approved. Users can now connect." };
  if (status !== "error") return { tone: "error", message: "Couldn't connect Microsoft 365" };

  switch (reason) {
    case "access_denied":
      return { tone: "quiet", message: "Microsoft sign-in was cancelled." };
    case "consent_required":
      return {
        tone: "error",
        message: "Your organisation requires admin approval.",
        offerAdmin: true,
      };
    case "MICROSOFT_PERSONAL_ACCOUNT":
      return { tone: "error", message: "Use a work Microsoft 365 account, or connect Google" };
    case "MICROSOFT_MAILBOX_UNAVAILABLE":
      return { tone: "error", message: "This account has no Outlook mailbox" };
    case "MICROSOFT_TEAMS_NOT_AVAILABLE":
      return { tone: "error", message: "Teams isn't enabled for your organisation" };
    case "MICROSOFT_ADMIN_CONSENT_DENIED":
      return { tone: "error", message: "Your admin declined access" };
    case "MICROSOFT_SCOPES_MISSING":
      return { tone: "error", message: "Please allow all requested permissions", retry: true };
    case "OAUTH_STATE_INVALID":
      return { tone: "error", message: "Link expired, try again" };
    case "MICROSOFT_NO_REFRESH_TOKEN":
    case "OAUTH_CODE_MISSING":
    case "MICROSOFT_API_ERROR":
    case "INTERNAL_ERROR":
      return { tone: "error", message: "Couldn't connect, try again" };
    default:
      return { tone: "error", message: "Couldn't connect Microsoft 365" };
  }
};

const normalizeConnection = (raw) => {
  if (!raw || typeof raw !== "object") {
    return { configured: false, status: INTEGRATION_STATUS.NOT_CONNECTED, accountEmail: "" };
  }
  return {
    configured: raw.configured !== false,
    status: raw.status || (raw.connected ? INTEGRATION_STATUS.CONNECTED : INTEGRATION_STATUS.NOT_CONNECTED),
    accountEmail: raw.account_email || "",
  };
};

// Meeting integrations. GET /calendar/connections is the source of truth for both providers.
export const fetchIntegrationStatus = async (provider) => {
  const response = await api.get(`${BASE_URL}/api/${provider}/status`);
  return response.data?.status || (response.data?.connected ? INTEGRATION_STATUS.CONNECTED : INTEGRATION_STATUS.NOT_CONNECTED);
};

export const fetchGoogleStatus = () => fetchIntegrationStatus(INTEGRATION_PROVIDER.GOOGLE);

export const fetchCalendarConnections = async () => {
  try {
    const response = await api.get(`${BASE_URL}/api/calendar/connections`);
    const connections = response.data?.connections || {};
    return {
      google: normalizeConnection(connections.google),
      microsoft: connections.microsoft
        ? normalizeConnection(connections.microsoft)
        : { configured: false, status: INTEGRATION_STATUS.NOT_CONNECTED, accountEmail: "" },
    };
  } catch (error) {
    // An older server without the combined endpoint should still show Google.
    if (error?.response?.status !== 404) throw error;
    const status = await fetchGoogleStatus().catch(() => INTEGRATION_STATUS.NOT_CONNECTED);
    return {
      google: { configured: true, status, accountEmail: "" },
      microsoft: { configured: false, status: INTEGRATION_STATUS.NOT_CONNECTED, accountEmail: "" },
    };
  }
};

export const getIntegrationConnectUrl = async (provider) => {
  const response = await api.get(`${BASE_URL}/api/${provider}/oauth/connect`);
  return response.data?.authorization_url;
};

export const getGoogleConnectUrl = () => getIntegrationConnectUrl(INTEGRATION_PROVIDER.GOOGLE);

export const getMicrosoftAdminConsentUrl = async () => {
  const response = await api.get(`${BASE_URL}/api/microsoft/admin-consent`);
  return response.data?.admin_consent_url;
};

export const disconnectIntegration = async (provider) => {
  const response = await api.delete(`${BASE_URL}/api/${provider}/connection`);
  return response.data;
};

export const disconnectGoogleCalendar = () => disconnectIntegration(INTEGRATION_PROVIDER.GOOGLE);

export const bookableProviderIds = (connections, { hideMicrosoft = false } = {}) => {
  if (!connections) return [];
  return CALENDAR_PROVIDERS.map((item) => item.id).filter((id) => {
    if (hideMicrosoft && id === INTEGRATION_PROVIDER.MICROSOFT) return false;
    const connection = connections[id];
    return connection?.configured !== false && connection?.status === INTEGRATION_STATUS.CONNECTED;
  });
};

export const visibleProviderIds = (connections, { hideMicrosoft = false } = {}) => {
  if (!connections) return [];
  return CALENDAR_PROVIDERS.map((item) => item.id).filter((id) => {
    if (hideMicrosoft && id === INTEGRATION_PROVIDER.MICROSOFT) return false;
    return connections[id]?.configured !== false;
  });
};

// Rounds
export const fetchInterviewRounds = async () => {
  const response = await api.get(`${BASE_URL}/api/interview-rounds`);
  return (response.data?.rounds || []).map(normalizeRound);
};

export const createInterviewRound = async (name) => {
  const response = await api.post(`${BASE_URL}/api/interview-rounds`, { name });
  return normalizeRound(response.data?.round || response.data || {});
};

// Panelists (interviewers)
export const fetchPanelists = async () => {
  const response = await api.get(`${BASE_URL}/api/interview-panelists`, {
    params: { page: 1, limit: 50 },
  });
  return (response.data?.panelists || []).map(normalizePanelist);
};

// Interviews
const fetchInterviewsByStatus = async (statuses, filters = {}) => {
  const interviews = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await api.get(`${BASE_URL}/api/interviews`, {
      params: {
        ...filters,
        status: statuses.join(","),
        page,
        limit: PAGE_LIMIT,
      },
    });
    const pageItems = response.data?.interviews || [];
    interviews.push(...pageItems.map(normalizeInterview));
    if (pageItems.length === 0 || interviews.length >= (response.data?.count ?? 0)) break;
  }
  return interviews;
};

// Set of candidate ids with an active interview, optionally for a single JD
export const fetchScheduledCandidateIds = async (jdId) => {
  const interviews = await fetchInterviewsByStatus(
    ACTIVE_INTERVIEW_STATUSES,
    jdId ? { jd_id: jdId } : {}
  );
  return new Set(interviews.map((interview) => interview.candidateId).filter(Boolean));
};

const zoneFormatParts = (date, timeZone, options) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: timeZone || undefined,
      hourCycle: "h23",
    }).formatToParts(date).map((part) => [part.type, part.value])
  );
  if (Number(parts.hour) === 24) parts.hour = "00";
  return parts;
};

const zoneOffsetMinutes = (date, timeZone) => {
  const parts = zoneFormatParts(date, timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const zonedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute)
  );
  return Math.round((zonedAsUtc - date.getTime()) / 60000);
};

// Wall-clock date (YYYY-MM-DD) and time (HH:mm) in timeZone, as an absolute instant.
export const zonedDateTime = (date, time, timeZone) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date || "").slice(0, 10));
  const clock = /^(\d{2}):(\d{2})/.exec(time || "");
  if (!match || !clock) return new Date(NaN);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(clock[1]);
  const minute = Number(clock[2]);
  const zone = (timeZone || "").trim();
  const asLocal = () => new Date(year, month - 1, day, hour, minute, 0, 0);
  if (!zone) return asLocal();
  try {
    Intl.DateTimeFormat(undefined, { timeZone: zone });
  } catch {
    return asLocal();
  }
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const firstOffset = zoneOffsetMinutes(new Date(wallAsUtc), zone);
  let instant = wallAsUtc - firstOffset * 60000;
  const secondOffset = zoneOffsetMinutes(new Date(instant), zone);
  if (secondOffset !== firstOffset) instant = wallAsUtc - secondOffset * 60000;
  return new Date(instant);
};

export const calendarDateInZone = (timeZone, date = new Date()) => {
  try {
    const parts = zoneFormatParts(date, (timeZone || "").trim(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  }
};

export const clockInZone = (timeZone, date = new Date()) => {
  try {
    const parts = zoneFormatParts(date, (timeZone || "").trim(), {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${parts.hour}:${parts.minute}`;
  } catch {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  }
};

const nextMinuteInstant = () => new Date(Math.floor(Date.now() / 60000) * 60000 + 60000);

export const earliestBookableDate = (timeZone) => calendarDateInZone(timeZone, nextMinuteInstant());

export const earliestBookableClock = (timeZone) => clockInZone(timeZone, nextMinuteInstant());

// `endDate` (defaults to `date`) is the day the interview ends, for one that runs past midnight
export const scheduleSlotError = (date, startTime, endTime, timeZone, endDate = date) => {
  const hasDate = /^\d{4}-\d{2}-\d{2}$/.test((date || "").slice(0, 10));
  const hasStart = /^\d{2}:\d{2}/.test(startTime || "");
  const hasEnd = /^\d{2}:\d{2}/.test(endTime || "");
  const missing = [
    !hasDate && "Enter a date.",
    !hasStart && "Enter a start time.",
    !hasEnd && "Enter an end time.",
  ].filter(Boolean);
  if (missing.length) return missing.join(" ");
  const start = zonedDateTime(date, startTime, timeZone);
  const end = zonedDateTime(endDate || date, endTime, timeZone);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "Please enter a valid date and time.";
  }
  const selectedDate = (date || "").slice(0, 10);
  if (selectedDate < calendarDateInZone(timeZone)) return "Date must be today or in the future.";
  if (start.getTime() <= Date.now()) return "Time must be in the future.";
  if (end.getTime() <= start.getTime()) return "End time must be after the start time.";
  return "";
};

// Changes only the AI mode, without rescheduling. A booked interview's invite gets the AI bot added or removed.
// Errors: 409 INVALID_STATUS_TRANSITION (not pending/scheduled/rescheduled), 422 VALIDATION_ERROR.
/** @param {InterviewAIMode} aiMode @returns {Promise<Interview>} */
export const setInterviewAIMode = async (interviewId, aiMode) => {
  const response = await api.post(`${BASE_URL}/api/interviews/${interviewId}/ai-mode`, { ai_mode: aiMode ?? null });
  return response.data?.interview;
};

// Interview feedback: one per panelist per interview. Allowed once the interview is scheduled.
export const FEEDBACK_RESULTS = ["cleared", "neutral", "rejected"];
export const FEEDBACK_ALLOWED_STATUSES = ["scheduled", "rescheduled", "completed"];

// Everything the form needs: interview, can_add_feedback, results, panelists (with has_feedback), default skill rows
export const fetchFeedbackForm = async (interviewId) => {
  const response = await api.get(`${BASE_URL}/api/interviews/${interviewId}/feedback-form`);
  return response.data || {};
};

// All feedback for one interview, plus the server's summary
// (average_rating, result_counts, feedback_count, panelist_count, pending_panelists)
export const fetchInterviewFeedback = async (interviewId) => {
  const response = await api.get(`${BASE_URL}/api/interviews/${interviewId}/feedback`);
  return {
    feedback: response.data?.feedback || [],
    summary: response.data?.summary || null,
  };
};

// Creates or replaces this panelist's feedback; send the whole form every time
export const saveInterviewFeedback = async (interviewId, panelistId, { result, skillAssessment, feedback }) => {
  const response = await api.put(`${BASE_URL}/api/interviews/${interviewId}/feedback/${panelistId}`, {
    result,
    skill_assessment: skillAssessment,
    feedback,
  });
  return response.data?.feedback;
};

export const deleteInterviewFeedback = async (interviewId, panelistId) => {
  const response = await api.delete(`${BASE_URL}/api/interviews/${interviewId}/feedback/${panelistId}`);
  return response.data;
};

// e.g. "skill_assessment[1].rating" → 1, so the form can highlight that row
export const feedbackErrorRow = (error) => {
  const match = /^skill_assessment\[(\d+)\]/.exec(error?.response?.data?.details?.field || "");
  return match ? Number(match[1]) : null;
};
